import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, BookMarked, Loader, Search, X } from 'lucide-react';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { scheduledCommandsApi } from '../../../services/scheduledCommandsApi';
import type { TellrawLibraryEntry, TellrawLibraryInput } from '../../../types/scheduledCommands';
import { compileTellraw } from '../../../utils/tellraw';
import { TellrawPreview } from './TellrawPreview';
import { SkeletonLoader } from '../../SkeletonLoader';

interface SaveToLibraryModalProps {
  defaultName: string;
  /** Everything but the name and notes, which are asked for here. */
  entry: Omit<TellrawLibraryInput, 'name' | 'notes'>;
  onClose: () => void;
  onSaved: (entry: TellrawLibraryEntry) => void;
}

/** Keep a message that works, under a name, for any schedule to reuse. */
export const SaveToLibraryModal = ({ defaultName, entry, onClose, onSaved }: SaveToLibraryModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useOnClickOutside(modalRef, () => !saving && onClose());

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await scheduledCommandsApi.saveToLibrary({ ...entry, name: name.trim(), notes: notes.trim() || null });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It could not be saved.');
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-lg" role="dialog" aria-modal="true" aria-labelledby="save-library-title">
        <div className="modal-header">
          <div>
            <h2 id="save-library-title" className="modal-title">Save to the tellraw library</h2>
            <p className="modal-subtitle">A copy, with its conversation, that any schedule can start from.</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close" disabled={saving}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="modal-body space-y-4">
          <TellrawPreview component={compileTellraw(entry.message, entry.compat).component} />
          <div>
            <label htmlFor="library-name" className="block text-xs font-medium text-glass-400 mb-1.5">Name</label>
            <input
              id="library-name"
              className="glass-input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoFocus
              placeholder="e.g. Store sale, gold"
            />
          </div>
          <div>
            <label htmlFor="library-notes" className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
            <textarea
              id="library-notes"
              className="glass-input w-full min-h-[4.5rem]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              placeholder="When to use it, what to change before reusing it…"
            />
          </div>
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="glass-button-secondary" disabled={saving}>Cancel</button>
          <button type="button" onClick={() => void save()} className="btn-primary" disabled={saving || !name.trim()}>
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <BookMarked className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

interface LibraryPickerModalProps {
  /** Say that picking replaces what is there, when there is something. */
  replacing: boolean;
  onClose: () => void;
  onPick: (entry: TellrawLibraryEntry) => void;
}

/** Start from a saved tellraw. The pick is a copy: editing it here leaves the library entry alone. */
export const LibraryPickerModal = ({ replacing, onClose, onPick }: LibraryPickerModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [entries, setEntries] = useState<TellrawLibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState<string | null>(null);
  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    scheduledCommandsApi
      .library()
      .then(setEntries)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'The library could not be loaded.'));
  }, []);

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (entries ?? []).filter((e) => !query || e.name.toLowerCase().includes(query) || (e.notes ?? '').toLowerCase().includes(query));
  }, [entries, search]);

  // The list omits conversations; fetch the one picked so follow-ups keep their context.
  const pick = async (entry: TellrawLibraryEntry) => {
    setPicking(entry.id);
    try {
      onPick(await scheduledCommandsApi.libraryEntry(entry.id));
    } catch {
      onPick(entry);
    }
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-2xl" role="dialog" aria-modal="true" aria-labelledby="library-picker-title">
        <div className="modal-header">
          <div>
            <h2 id="library-picker-title" className="modal-title">Start from the library</h2>
            <p className="modal-subtitle">
              {replacing ? 'Replaces the current message and conversation with a copy of the one you pick.' : 'A copy of the one you pick is loaded into the composer.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="modal-body space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input className="glass-input w-full pl-9" placeholder="Search saved tellraws…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus aria-label="Search saved tellraws" />
          </div>
          {error ? (
            <p className="text-sm text-red-400">{error}</p>
          ) : !entries ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} className="h-16" />)}</div>
          ) : shown.length === 0 ? (
            <p className="text-sm text-glass-400 py-4 text-center">{entries.length ? 'Nothing matches that search.' : 'The library is empty so far.'}</p>
          ) : (
            <ul className="space-y-2">
              {shown.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => void pick(entry)}
                    disabled={picking !== null}
                    className="w-full subcard p-3 text-left space-y-2 hover:border-glass-200/25 transition-colors disabled:opacity-60"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-white truncate">{entry.name}</span>
                      {picking === entry.id && <Loader className="w-4 h-4 animate-spin text-glass-400" />}
                    </span>
                    <TellrawPreview component={compileTellraw(entry.message, entry.compat).component} compact className="max-w-full" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
